import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isTr = language === 'tr';

  const features = [
    {
      letter: 'A',
      title: isTr ? 'Hızlı Analiz' : 'Fast Analysis',
      description: isTr
        ? 'Senaryolarını dakikalar içinde kapsamlı şekilde analiz et. Yapay zekâ destekli motor, sayfayı sindirir ve detaylı rapor verir.'
        : 'Analyze your scripts comprehensively in minutes. The AI-powered engine reads the page and returns a detailed report.',
    },
    {
      letter: 'P',
      title: isTr ? 'Puanlama' : 'Scoring',
      description: isTr
        ? 'Yedi eksen — konseptten ticari potansiyele — her birinin kendi mantığı, kendi gerekçesi.'
        : 'Seven axes — from concept to commercial potential — each with its own logic, its own rationale.',
    },
    {
      letter: 'K',
      title: isTr ? 'Karakter Ağı' : 'Character Network',
      description: isTr
        ? 'Her karakterin tüm verisi ayrı dosya. İlişkiler tablo halinde — tip ve yoğunluk dahil.'
        : 'Every character a separate file. Relationships in table form — type and intensity included.',
    },
    {
      letter: 'S',
      title: isTr ? 'Sıralı Döküm' : 'Sequential Breakdown',
      description: isTr
        ? 'Sahne sahne, yapım dilinde tablo. Mekan, zaman, sayfa, oyuncu, yardımcı, özet — Excele iner.'
        : 'Scene by scene, in production language. Location, time, page, cast, support, summary — exports to Excel.',
    },
    {
      letter: 'M',
      title: isTr ? 'Mekan Dökümü' : 'Location Breakdown',
      description: isTr
        ? 'Tüm mekanlar tabloda. İç/dış, gün/gece, sahne sayısı, karakterler, prodüksiyon notları.'
        : 'Every location in a table. Int/Ext, day/night, scene count, characters, production notes.',
    },
    {
      letter: 'R',
      title: isTr ? 'On Bölümlük Rapor' : 'Ten-Chapter Report',
      description: isTr
        ? 'Konseptten yapıcı geliştirme planına. Tek bir uzun rapor, PDF olarak indirilir.'
        : 'From concept to constructive development plan. A single long report, downloadable as PDF.',
    },
    {
      letter: 'V',
      title: isTr ? 'Seslendirme' : 'Voiceover',
      description: isTr
        ? 'Karakter başına ses ataması. Senaryon yapay zekâ ile okunur, kayda alınır.'
        : 'Voice assignment per character. Your script read aloud by AI and recorded.',
    },
    {
      letter: 'L',
      title: isTr ? 'Çoklu Dil' : 'Multi-language',
      description: isTr
        ? 'Çıktıyı farklı dillerde alma seçeneği. Türkçe orijinal, İngilizce çeviri, ya da tersi.'
        : 'Receive output in multiple languages. Original Turkish, English translation — or vice versa.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
        {/* Section header */}
        <div className="flex items-baseline justify-between mb-12 max-w-6xl mx-auto">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'ÖZELLİKLER' : 'FEATURES'}</span>
          <span className="eyebrow-mute hidden md:inline">{isTr ? 'SEKİZ KAPI' : 'EIGHT GATES'}</span>
        </div>

        <div className="max-w-3xl mb-20">
          <span className="eyebrow mb-4 block">{isTr ? 'TEK SENARYO' : 'ONE SCRIPT'}</span>
          <h1 className="display-h display-lg mb-6">
            {isTr ? <>Bir senaryo. <em>Çok çıktı.</em></> : <>One script. <em>Many outputs.</em></>}
          </h1>
          <p className="font-prose text-[17px] leading-relaxed text-ink-dim max-w-2xl">
            {isTr
              ? 'Mr.Mirror, sayfayı yalnız okumaz — onu üretim toplantısının dilinde geri verir. Aşağıda her bir kapı, kendi başına çağrılabilir.'
              : "Mr.Mirror doesn't just read the page — it returns it in the language of a production meeting. Each gate below can be called on its own."}
          </p>
        </div>

        {/* Features grid - editorial cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px mb-24 bg-hairline border border-hairline">
          {features.map((feature, i) => (
            <div
              key={i}
              className="bg-background p-8 hover:bg-accent-soft transition-colors group cursor-default"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-serif italic text-5xl text-accent leading-none">
                  {feature.letter}
                </span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-mute">
                  /{String(i + 1).padStart(2, '0')}
                </span>
              </div>
              <h3 className="font-mono text-[12px] tracking-[0.1em] uppercase font-bold text-ink mb-3 leading-tight">
                {feature.title}
              </h3>
              <p className="font-prose text-[13px] leading-relaxed text-ink-dim">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center max-w-2xl mx-auto py-16 border-t border-hairline">
          <span className="eyebrow mb-4 block">{isTr ? 'BAŞLA' : 'BEGIN'}</span>
          <h2 className="display-h display-md mb-6">
            {isTr ? <>Senaryon, <em>aynaya</em> tutuluyor.</> : <>Your script, <em>held up</em> to the mirror.</>}
          </h2>
          <p className="font-prose text-[15px] leading-relaxed text-ink-dim mb-8">
            {isTr
              ? 'Yeni bir analiz başlat ya da mevcut analizlerini gör.'
              : 'Start a new analysis or view existing ones.'}
          </p>
          <button onClick={() => navigate('/login')} className="btn-accent">
            {t.startNow}
            <span aria-hidden>→</span>
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FeaturesPage;
